import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import {
  getAccountProfile,
  loginUser,
  logoutUser,
  registerUser,
} from "../../services/Auth/AuthServices";
import { mergeGuestCart, mergeGuestWishlist } from "../../services/GuestMerge/mergeGuestData";

// ─── Remember-me storage selector ────────────────────────────────────────────
const getStorageBasedOnRemember = () => {
  const rememberData = localStorage.getItem("auth-remember");
  if (rememberData) {
    try {
      const data = JSON.parse(rememberData);
      const now = Date.now();
      if (data.expiry && now < data.expiry && data.remember) {
        return localStorage;
      }
    } catch {}
  }
  return sessionStorage;
};

// ─── Auth Store ───────────────────────────────────────────────────────────────
const useAuthStore = create(
  persist(
    (set, get) => ({
      // ── State ──────────────────────────────────────────────────────────────
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      remember: false,

      // ── setRemember ────────────────────────────────────────────────────────
      setRemember: (value) => {
        set({ remember: value });
        if (value) {
          const currentState = get();
          const expiry = Date.now() + 14 * 24 * 60 * 60 * 1000;
          localStorage.setItem(
            "auth-remember",
            JSON.stringify({
              user: currentState.user,
              isAuthenticated: currentState.isAuthenticated,
              remember: true,
              expiry,
            })
          );
        } else {
          localStorage.removeItem("auth-remember");
        }
      },

      // ── checkAuthUser ──────────────────────────────────────────────────────
      checkAuthUser: async () => {
        set({ isLoading: true, error: null });
        try {
          const res = await getAccountProfile();

          if (res.success && res.user) {
            set({
              user: res.user,
              isAuthenticated: true,
              isLoading: false,
              error: null,
            });

            const currentState = get();
            if (currentState.remember) {
              const expiry = Date.now() + 14 * 24 * 60 * 60 * 1000;
              localStorage.setItem(
                "auth-remember",
                JSON.stringify({
                  user: res.user,
                  isAuthenticated: true,
                  remember: true,
                  expiry,
                })
              );
            }

            return { success: true, user: res.user };
          }

          if (
            res.code === "not_logged_in" ||
            (res.data && res.data.status === 401)
          ) {
            set({
              user: null,
              isAuthenticated: false,
              isLoading: false,
              error: null,
              remember: false,
            });
            localStorage.removeItem("auth-remember");
            return {
              success: false,
              error: res.message || "User is not logged in",
              needLogin: true,
            };
          }

          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: res.message || "Không thể xác thực người dùng",
          });
          return { success: false, error: res.message || "Authentication failed" };
        } catch (error) {
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: "Có lỗi xảy ra khi kiểm tra xác thực",
          });
          return { success: false, error: "Network error" };
        }
      },

      // ── restoreFromRemember ────────────────────────────────────────────────
      restoreFromRemember: () => {
        try {
          const rememberData = localStorage.getItem("auth-remember");
          if (rememberData) {
            const data = JSON.parse(rememberData);
            if (data.expiry && Date.now() > data.expiry) {
              localStorage.removeItem("auth-remember");
              return false;
            }
            set({
              user: data.user,
              isAuthenticated: data.isAuthenticated,
              remember: true,
            });
            return true;
          }
        } catch {
          localStorage.removeItem("auth-remember");
        }
        return false;
      },

      // ── login ──────────────────────────────────────────────────────────────
      login: async (credentials) => {
        set({ isLoading: true, error: null });
        try {
          const response = await loginUser(credentials);

          if (response?.success) {
            const currentState = get();

            set({
              user: response.user,
              isAuthenticated: true,
              isLoading: false,
              error: null,
            });

            if (currentState.remember) {
              const expiry = Date.now() + 14 * 24 * 60 * 60 * 1000;
              localStorage.setItem(
                "auth-remember",
                JSON.stringify({
                  user: response.user,
                  isAuthenticated: true,
                  remember: true,
                  expiry,
                })
              );
            }

            // ── Merge guest cart/wishlist sau login ──────────────────────────
            // Import lazy để tránh circular dependency với cart/wishlist stores
            try {
              const { default: useCartStore } = await import("../Cart/useCartStore");
              const { default: useWishlistStore } = await import("../WishList/WishListStore");

              const guestCart = useCartStore.getState()._readGuestCart();
              const guestWishlist = useWishlistStore.getState()._readGuestWishlist();

              // Merge cart
              if (guestCart && guestCart.length > 0) {
                try {
                  const cartResult = await mergeGuestCart(guestCart);
                  if (cartResult?.success === false) {
                    throw new Error(cartResult.message || "Không thể đồng bộ giỏ hàng");
                  }

                  const mergedIndexes = Array.isArray(cartResult?.merged)
                    ? cartResult.merged
                    : [];

                  // Chỉ xóa item đã merge thành công, giữ invalid/out_of_stock để user xử lý.
                  if (mergedIndexes.length > 0) {
                    useCartStore
                      .getState()
                      ._clearGuestCartItemsByIndexes(mergedIndexes);
                  }
                  useCartStore.getState().setGuestMode(false);
                  await useCartStore.getState().showCart();
                } catch {
                  // Silent: giữ guest cart nếu merge fail
                  useCartStore.getState().setGuestMode(false);
                  await useCartStore.getState().showCart();
                }
              } else {
                useCartStore.getState().setGuestMode(false);
                await useCartStore.getState().showCart();
              }

              // Merge wishlist
              if (guestWishlist && guestWishlist.length > 0) {
                try {
                  const productIds = [
                    ...new Set(guestWishlist.map((w) => Number(w.id)).filter(Boolean)),
                  ];
                  const wishlistResult = await mergeGuestWishlist(productIds);
                  if (wishlistResult?.success === false) {
                    throw new Error(
                      wishlistResult.message || "Không thể đồng bộ wishlist"
                    );
                  }

                  const clearProductIds = [
                    ...(Array.isArray(wishlistResult?.added)
                      ? wishlistResult.added
                      : []),
                    ...(Array.isArray(wishlistResult?.already_exists)
                      ? wishlistResult.already_exists
                      : []),
                  ];

                  // Giữ invalid để user có thể tự xóa sau.
                  if (clearProductIds.length > 0) {
                    useWishlistStore
                      .getState()
                      ._clearGuestWishlistItems(clearProductIds);
                  }
                  useWishlistStore.getState().setGuestMode(false);
                  await useWishlistStore.getState().ShowWishlist();
                } catch {
                  // Silent: giữ guest wishlist nếu merge fail
                  useWishlistStore.getState().setGuestMode(false);
                  await useWishlistStore.getState().ShowWishlist();
                }
              } else {
                useWishlistStore.getState().setGuestMode(false);
                await useWishlistStore.getState().ShowWishlist();
              }
            } catch (mergeError) {
              // Merge không thành công không nên block login
            }

            return { success: true, user: response.user };
          } else {
            set({
              isLoading: false,
              error: response?.message || "Đăng nhập thất bại",
            });
            return { success: false, error: response?.message };
          }
        } catch (error) {
          set({ isLoading: false, error: "Có lỗi xảy ra khi đăng nhập" });
          return { success: false, error: "Network error" };
        }
      },

      // ── register ───────────────────────────────────────────────────────────
      register: async (userData) => {
        set({ isLoading: true, error: null });
        try {
          const response = await registerUser(userData);
          if (response?.success) {
            set({ isLoading: false, error: null });
            return { success: true, message: response.message };
          } else {
            set({
              isLoading: false,
              error: response?.message || "Đăng ký thất bại",
            });
            return { success: false, error: response?.message };
          }
        } catch (error) {
          set({ isLoading: false, error: "Có lỗi xảy ra khi đăng ký" });
          return { success: false, error: "Network error" };
        }
      },

      // ── logout ─────────────────────────────────────────────────────────────
      logout: async () => {
        await logoutUser();

        set({
          user: null,
          isAuthenticated: false,
          error: null,
          remember: false,
        });

        localStorage.removeItem("auth-remember");

        // Switch stores về guest mode sau logout
        try {
          const { default: useCartStore } = await import("../Cart/useCartStore");
          const { default: useWishlistStore } = await import("../WishList/WishListStore");

          useCartStore.getState().setGuestMode(true);
          useCartStore.getState().reset();
          await useCartStore.getState().showCart();

          useWishlistStore.getState().setGuestMode(true);
          useWishlistStore.getState().reset();
          await useWishlistStore.getState().ShowWishlist();
        } catch {}
      },

      // ── clearError ─────────────────────────────────────────────────────────
      clearError: () => set({ error: null }),

      // ── updateUser ─────────────────────────────────────────────────────────
      updateUser: (userData) =>
        set((state) => ({ user: { ...state.user, ...userData } })),

      markUnauthenticated: () => {
        localStorage.removeItem("auth-remember");
        set({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          error: null,
          remember: false,
        });
      },

      // ── checkAuthExpiry ────────────────────────────────────────────────────
      checkAuthExpiry: () => {
        try {
          const rememberData = localStorage.getItem("auth-remember");
          if (rememberData) {
            const data = JSON.parse(rememberData);
            if (data.expiry && Date.now() > data.expiry) {
              localStorage.removeItem("auth-remember");
              set({
                user: null,
                isAuthenticated: false,
                error: null,
                remember: false,
              });
              return false;
            }
            return true;
          }
        } catch {
          localStorage.removeItem("auth-remember");
        }
        return get().isAuthenticated;
      },
    }),
    {
      name: "auth-storage",
      storage: createJSONStorage(() => getStorageBasedOnRemember()),
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        remember: state.remember,
      }),
    }
  )
);

export default useAuthStore;
